import { Integration } from '../../lib/Constants';
import {
  checkEnvBuildPlugin,
  createIsolatedTestEnv,
  checkFileContents,
  checkFileExists,
  checkIfBuilds,
  checkIfRunsOnDevMode,
  checkIfRunsOnProdMode,
  checkPackageJson,
  getWizardCommand,
} from '../utils';
import { afterAll, beforeAll, describe, expect, test } from 'vitest';

//@ts-expect-error - clifty is ESM only
import { KEYS, withEnv } from 'clifty';

describe('NextJS-14', () => {
  const integration = Integration.nextjs;
  let wizardExitCode: number;

  const { projectDir, cleanup } = createIsolatedTestEnv('nextjs-14-test-app');

  beforeAll(async () => {
    wizardExitCode = await withEnv({
      cwd: projectDir,
    })
      .defineInteraction()
      .whenAsked('Please select your package manager.')
      .respondWith(KEYS.DOWN, KEYS.ENTER) // Select yarn
      .expectOutput('Installing @sentry/nextjs')
      .whenAsked(
        'Do you want to route Sentry requests in the browser through your Next.js server',
        {
          timeout: 240_000, // package installation can take a while in CI
        },
      )
      .respondWith(KEYS.ENTER)
      .whenAsked('to track the performance of your application?')
      .respondWith(KEYS.ENTER)
      .whenAsked(
        'to get a video-like reproduction of errors during a user session?',
      )
      .respondWith(KEYS.ENTER)
      .whenAsked('to send your application logs to Sentry?')
      .respondWith(KEYS.ENTER)
      .whenAsked('Do you want to create an example page')
      .respondWith(KEYS.ENTER)
      .whenAsked('Are you using a CI/CD tool')
      .respondWith(KEYS.DOWN, KEYS.ENTER) // Select No
      .whenAsked(
        'Optionally add a project-scoped MCP server configuration for the Sentry MCP?',
      )
      .respondWith(KEYS.DOWN, KEYS.ENTER) // Decline MCP config
      .expectOutput('Successfully installed the Sentry Next.js SDK!')
      .run(getWizardCommand(integration));
  });

  afterAll(() => {
    cleanup();
  });

  test('exits with exit code 0', () => {
    expect(wizardExitCode).toBe(0);
  });

  test('package.json is updated correctly', () => {
    checkPackageJson(projectDir, '@sentry/nextjs');
  });

  test('.env-sentry-build-plugin is created and contains the auth token', () => {
    checkEnvBuildPlugin(projectDir);
  });

  test('example page exists', () => {
    checkFileExists(`${projectDir}/src/app/layout.tsx`);
    checkFileExists(`${projectDir}/src/app/sentry-example-page/page.tsx`);
    checkFileExists(`${projectDir}/src/app/api/sentry-example-api/route.ts`);
  });

  test('config files created', () => {
    checkFileExists(`${projectDir}/sentry.server.config.ts`);
    checkFileExists(`${projectDir}/sentry.edge.config.ts`);
  });

  test('global error file exists', () => {
    checkFileExists(`${projectDir}/src/app/global-error.tsx`);
  });

  test('instrumentation files exists', () => {
    checkFileExists(`${projectDir}/src/instrumentation.ts`);
    checkFileExists(`${projectDir}/src/instrumentation-client.ts`);
  });

  test('instrumentation file contains Sentry initialization', () => {
    checkFileContents(`${projectDir}/src/instrumentation.ts`, [
      'import * as Sentry from "@sentry/nextjs";',
      `export async function register() {
  if (process.env.NEXT_RUNTIME === "nodejs") {
    await import("../sentry.server.config");
  }

  if (process.env.NEXT_RUNTIME === "edge") {
    await import("../sentry.edge.config");
  }
}

export const onRequestError = Sentry.captureRequestError;`,
    ]);
  });

  test('next.config file contains Sentry wrapper', () => {
    checkFileContents(`${projectDir}/next.config.mjs`, [
      "import { withSentryConfig } from '@sentry/nextjs'",
      'export default withSentryConfig(nextConfig, {',
    ]);
  });

  test('runs on dev mode correctly', async () => {
    await checkIfRunsOnDevMode(projectDir, 'Ready in');
  });

  test('builds correctly', async () => {
    await checkIfBuilds(projectDir);
  });

  test('runs on prod mode correctly', async () => {
    await checkIfRunsOnProdMode(projectDir, 'Ready in');
  });

  test('root layout contains generateMetadata function', () => {
    checkFileContents(`${projectDir}/src/app/layout.tsx`, [
      "// This file was generated by the Sentry wizard because we couldn't find a root layout file.",
      "import * as Sentry from '@sentry/nextjs';",
      "import type { Metadata } from 'next';",
      '',
      'export function generateMetadata(): Metadata {',
      '  return {',
      '    other: {',
      '      ...Sentry.getTraceData(),',
      '    }',
      '  }',
      '};',
      '',
      'export default function RootLayout({',
      '  children,',
      '}: {',
      '  children: React.ReactNode',
      '}) {',
      '  return (',
      '    <html lang="en">',
      '      <body>{children}</body>',
      '    </html>',
      '  )',
      '}',
    ]);
  });
});
